import React from 'react';
import { isChunkLoadError, reloadOnceForStaleChunk } from '../utils/chunkReload';

/**
 * App-wide error boundary. A thrown render error used to white-screen the whole
 * app (e.g. a bad AI generation response) — now it shows a recovery card instead,
 * so a hiccup never blanks the screen mid-use.
 *
 * Stale-chunk errors are handled separately: when a lazily-loaded view fails to fetch
 * its chunk (common right after a deploy), we reload to the fresh bundle and show a
 * neutral "updating" state rather than the branded error card. This also covers the
 * top-level routes that use plain lazy() (lazyWithReload handles the tab views). If a
 * reload was already tried and the chunk STILL won't load, it's genuinely broken, so
 * we fall back to the recovery card instead of spinning forever.
 */
interface State { hasError: boolean; message?: string; updating: boolean }

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, updating: false };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : undefined,
      updating: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: unknown, info: unknown) {
    console.error('ErrorBoundary caught:', error, info);
    if (isChunkLoadError(error)) {
      // Reload to the fresh bundle. If the guard suppressed the reload (we already
      // reloaded recently and it still failed), it's a real break — show the card.
      const reloading = reloadOnceForStaleChunk();
      if (!reloading) this.setState({ updating: false });
    }
  }

  render() {
    if (this.state.updating) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#59A8DA] to-[#5FA660] flex items-center justify-center text-white font-black mb-1">LUC</div>
          <div className="w-6 h-6 border-2 border-gray-300 border-t-[#59A8DA] rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Updating to the latest version…</p>
        </div>
      );
    }
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[#59A8DA] to-[#5FA660] flex items-center justify-center text-white font-black text-lg mb-4">LUC</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-500 mb-6">
            That action hit a snag. Reload to recover your saved work. Unsaved changes may need to be entered again.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, updating: false }); window.location.reload(); }}
            className="px-6 py-2.5 bg-gradient-to-r from-[#59A8DA] to-[#5FA660] text-white rounded-xl font-semibold hover:opacity-90"
          >
            Reload
          </button>
          {this.state.message && (
            <p className="text-[11px] text-gray-400 mt-4 break-words">{this.state.message}</p>
          )}
        </div>
      </div>
    );
  }
}
